import { SubmitHandler, useForm } from "react-hook-form";
import { useEffect } from "react";
import { JsonEditor } from "json-edit-react";
import { toggleInvalidInput } from "../../core/helpers";
import BaseModal from "../common/BaseModal/BaseModal";
import { IBaseModalProps } from "../../core/types/elements/IModal";
import { SharedConfigFormData } from "./types";

const CreateEditSharedConfigModal = ({
  showModal,
  onCancel,
  onClose,
  onSave,
  selectedEntity,
  defaultValues,
}: IBaseModalProps<SharedConfigFormData> & { selectedEntity: any; defaultValues: any }) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    setValue,
  } = useForm<SharedConfigFormData>({ defaultValues });

  const modalBody = () => {
    return (
      <form className="notification-modal">
        <div className="mb-3">
          <label htmlFor="name" className="form-label required">
            Name
          </label>
          <input
            type="text"
            className={`form-control ${toggleInvalidInput(!!errors.name)}`}
            id="name"
            placeholder="Enter Name"
            {...register("name", { required: true })}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="configuration" className="form-label">
            Configuration
          </label>
          <JsonEditor
            data={defaultValues ? defaultValues.configuration : selectedEntity.configuration}
            showCollectionCount={false}
            rootName=""
            maxWidth="100%"
            onEdit={({ newData }) => setValue("configuration", newData)}
            enableClipboard={false}
            restrictAdd={() => true}
            restrictDelete={() => true}
            restrictTypeSelection={() => true}
            restrictEdit={({ value }) => {
              const isPrimitive =
                value === null || ["string", "number", "boolean"].includes(typeof value);
              return !isPrimitive;
            }}
          />
        </div>
        <div className="mb-3 form-check form-switch ps-3 d-flex align-items-center">
          <label className="form-check-label me-5" htmlFor="isEnabled">
            Activate
          </label>
          <input
            className="form-check-input"
            type="checkbox"
            id="isEnabled"
            {...register("isEnabled")}
          />
        </div>
      </form>
    );
  };

  const onCancelModal = () => {
    reset(undefined);
    if (onCancel) onCancel();
  };

  const onSubmit: SubmitHandler<SharedConfigFormData> = async formData => {
    const data = {
      id: selectedEntity?.id,
      ...formData,
    };
    if (onSave) {
      onSave(data, !(!selectedEntity && onSave), () => {
        setValue("name", "");
        setValue("configuration", undefined);
        setValue("isEnabled", true);
      });
    }
  };

  useEffect(() => {
    if (selectedEntity) {
      setValue("name", selectedEntity?.name);
      setValue(
        "configuration",
        selectedEntity.configuration ? selectedEntity.configuration : defaultValues.configuration,
      );
      setValue("isEnabled", selectedEntity?.isEnabled);
    }
  }, [selectedEntity]);

  return (
    <BaseModal
      modalTitle={selectedEntity ? "Edit Shared Config" : "Create Shared Config"}
      onClose={onClose}
      onCancel={onCancelModal}
      showModal={showModal}
      modalBody={modalBody()}
      onSave={handleSubmit(onSubmit)}
      cancelActionBtnText="Cancel"
      successBtnText={selectedEntity ? "Update" : "Create"}
      disableCloseActions
    />
  );
};

export default CreateEditSharedConfigModal;
